import {
    Alert, Box, Chip, Grid, Paper, Skeleton, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Tooltip, Typography, IconButton,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DashboardLayout from '@examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from '@examples/Navbars/DashboardNavbar';
import MDButton from '@components/MDButton';
import { iconSize } from 'assets/theme/iconSize';
import { setLoading, useMaterialUIController } from 'context';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAlert } from '@components/showAlert/useAlertHook';
import { apiErrorMessage } from '@functions/api-error-message';
import { WhatsappTemplateService } from './services/whatsapp-template.service';
import {
    WhatsappConnectionResponse,
    WhatsappTemplateResponse,
} from './services/responses/whatsapp-template.response';
import { WhatsappTemplateModal } from './components/whatsapp-template.modal';

// A cor diz o que dá para usar: só o aprovado inicia conversa.
const statusColor = (status: string): 'success' | 'warning' | 'error' | 'default' => {
    const value = status.toUpperCase();

    if (value === 'APPROVED') return 'success';
    if (value === 'PENDING' || value === 'IN_APPEAL') return 'warning';
    if (value === 'REJECTED' || value === 'DISABLED' || value === 'PAUSED') return 'error';

    return 'default';
};

export const WhatsappTemplates = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [controller, dispatch] = useMaterialUIController();
    const { sidenavColor } = controller;
    const { showAlert, alertDelete } = useAlert();
    const service = new WhatsappTemplateService();

    const [templates, setTemplates] = useState<WhatsappTemplateResponse[] | null>(null);
    const [connection, setConnection] = useState<WhatsappConnectionResponse | null>(null);
    const [testing, setTesting] = useState(false);
    const [openModal, setOpenModal] = useState(false);
    const [editItem, setEditItem] = useState<WhatsappTemplateResponse | null>(null);

    const load = async () => {
        setTemplates(null);

        try {
            const res = await service.getList();
            const payload = res.data.data;

            setConnection(payload?.connection ?? null);
            setTemplates(payload?.templates ?? []);
        } catch (err) {
            setTemplates([]);
            showAlert(apiErrorMessage(err), 'error', 5000);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const remove = (name: string) => {
        setLoading(dispatch, true);
        service
            .deleteByName(name)
            .then(() => {
                showAlert(t('whatsappTemplates.excluirSucesso'), 'success', 2500);
                load();
            })
            .catch((err) => showAlert(apiErrorMessage(err), 'error', 4000))
            .finally(() => setLoading(dispatch, false));
    };

    const testConnection = async () => {
        setTesting(true);

        try {
            const res = await service.testConnection();

            showAlert(
                t('whatsappTemplates.conexaoOk', { total: res.data.data.count }),
                'success',
                4000
            );
        } catch (err) {
            showAlert(apiErrorMessage(err), 'error', 6000);
        } finally {
            setTesting(false);
        }
    };

    const openNew = () => {
        setEditItem(null);
        setOpenModal(true);
    };

    const openEdit = (item: WhatsappTemplateResponse) => {
        setEditItem(item);
        setOpenModal(true);
    };

    return (
        <DashboardLayout>
            <DashboardNavbar title={t('whatsappTemplates.titulo')} />

            <Paper sx={{ width: '100%', overflow: 'hidden', mt: 2 }}>
                <Box sx={{ p: 3 }}>
                    <Grid container alignItems="flex-start" spacing={2}>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Typography variant="h6">
                                {t('whatsappTemplates.conexaoTitulo')}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                                {t('whatsappTemplates.conexaoSubtitulo')}
                            </Typography>
                        </Grid>
                        <Grid
                            size={{ xs: 12, md: 6 }}
                            sx={{
                                display: 'flex',
                                gap: 1,
                                alignItems: 'center',
                                justifyContent: { xs: 'flex-start', md: 'flex-end' },
                                flexWrap: 'wrap',
                            }}
                        >
                            <Chip
                                size="small"
                                color={connection?.configured ? 'success' : 'default'}
                                label={
                                    connection?.configured
                                        ? t('whatsappTemplates.conectado')
                                        : t('whatsappTemplates.semConexao')
                                }
                            />
                            <MDButton
                                variant="gradient"
                                color="dark"
                                size="small"
                                onClick={testConnection}
                                disabled={!connection?.configured || testing}
                            >
                                {t('whatsappTemplates.testarConexao')}
                            </MDButton>
                            <MDButton
                                variant="gradient"
                                color={sidenavColor}
                                size="small"
                                startIcon={<EditIcon />}
                                onClick={() => navigate('/configuracoes/integracoes?abrir=meta-whatsapp')}
                            >
                                {t('general.editar')}
                            </MDButton>
                        </Grid>
                    </Grid>

                    <Grid container spacing={3} sx={{ mt: 1 }}>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Typography variant="caption" color="text.secondary" display="block">
                                {t('whatsappTemplates.contaLabel')}
                            </Typography>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                {connection?.wabaId || '—'}
                            </Typography>
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Typography variant="caption" color="text.secondary" display="block">
                                {t('whatsappTemplates.tokenLabel')}
                            </Typography>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                {connection?.accessToken || '—'}
                            </Typography>
                        </Grid>
                    </Grid>
                </Box>
            </Paper>

            <Paper sx={{ width: '100%', overflow: 'hidden', mt: 2 }}>
                <Box sx={{ p: 3 }}>
                    <Grid container alignItems="center" spacing={2}>
                        <Grid size={{ xs: 12, md: 8 }}>
                            <Typography variant="h6">
                                {t('whatsappTemplates.modelosTitulo')}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                                {t('whatsappTemplates.modelosSubtitulo')}
                            </Typography>
                        </Grid>
                        <Grid size={{ xs: 12, md: 4 }} sx={{ textAlign: { md: 'right' } }}>
                            <MDButton
                                variant="gradient"
                                color={sidenavColor}
                                onClick={openNew}
                                disabled={!connection?.configured}
                            >
                                {t('general.adicionar')}
                            </MDButton>
                        </Grid>
                    </Grid>

                    {!connection?.configured && (
                        <Alert severity="info" sx={{ mt: 2 }}>
                            {t('whatsappTemplates.naoConfigurado')}
                        </Alert>
                    )}

                    <TableContainer sx={{ marginTop: '15px', maxHeight: '60vh', overflowY: 'auto' }}>
                        <Table size="medium">
                            <TableHead
                                sx={{ position: 'sticky', top: 0, backgroundColor: 'background.card' }}
                            >
                                <TableRow hover>
                                    <TableCell width="40%">{t('whatsappTemplates.nome')}</TableCell>
                                    <TableCell width="18%">{t('whatsappTemplates.categoria')}</TableCell>
                                    <TableCell width="12%">{t('whatsappTemplates.idioma')}</TableCell>
                                    <TableCell width="15%">{t('whatsappTemplates.situacao')}</TableCell>
                                    <TableCell width="15%" align="center">
                                        {t('general.acoes')}
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {templates ? (
                                    templates.length > 0 ? (
                                        templates.map((item) => (
                                            <TableRow hover key={item.id}>
                                                <TableCell sx={{ fontFamily: 'monospace' }}>
                                                    {/* Texto inteiro só na edição: na tabela empurrava as outras colunas para fora. */}
                                                    <Tooltip title={item.bodyText}>
                                                        <span>{item.name}</span>
                                                    </Tooltip>
                                                </TableCell>
                                                <TableCell>
                                                    {t(`whatsappTemplates.categorias.${item.category.toUpperCase()}`, item.category)}
                                                </TableCell>
                                                <TableCell>
                                                    {t(`whatsappTemplates.idiomas.${item.language}`, item.language)}
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        size="small"
                                                        label={t(`whatsappTemplates.status.${item.status.toUpperCase()}`, item.status)}
                                                        color={statusColor(item.status)}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                                                        <Tooltip title={t('general.editar')}>
                                                            <IconButton size="small" sx={{ p: 0.25 }} onClick={() => openEdit(item)}>
                                                                <EditIcon
                                                                    style={iconSize}
                                                                />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title={t('general.excluir')}>
                                                            <IconButton
                                                                size="small"
                                                                sx={{ p: 0.25 }}
                                                                onClick={() =>
                                                                    alertDelete(
                                                                        item.name,
                                                                        remove,
                                                                        item.name,
                                                                        t('whatsappTemplates.excluirConfirm')
                                                                    )
                                                                }
                                                            >
                                                                <DeleteIcon
                                                                    sx={iconSize}
                                                                    color="error"
                                                                />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    ) : (
                                        <TableRow hover>
                                            <TableCell colSpan={5} align="center">
                                                {t('general.nenhumRegistro')}
                                            </TableCell>
                                        </TableRow>
                                    )
                                ) : (
                                    Array.from({ length: 4 }, (_, i) => i).map((index) => (
                                        <TableRow hover key={index}>
                                            <TableCell colSpan={5}>
                                                <Skeleton height={30} variant="rectangular" />
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            </Paper>

            <WhatsappTemplateModal
                open={openModal}
                edit={editItem}
                onClose={() => setOpenModal(false)}
                onSaved={load}
            />
        </DashboardLayout>
    );
};
